import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MapPin, Clock } from 'lucide-react';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { RequirementsList } from '../../components/RequirementsList.tsx';
import { RatingForm } from '../../components/RatingForm.tsx';
import { HoursPanel } from '../../components/HoursPanel.tsx';
import { OrgReputation } from '../../components/ReliabilityBadges.tsx';
import { formatDateRange, formatEuros, MISSION_STATUS_LABELS } from '../../format.ts';
import type { Mission } from '../../types.ts';
import { BriefingView } from '../../components/BriefingView.tsx';
import { openConversation, MESSAGE_ERRORS } from '../../messages.ts';

const APPLY_ERRORS: Record<string, string> = {
  EXPERIENCE_REQUIRED: 'Pour candidater, déclarez d’abord au moins une expérience avec un référent (nom, entreprise, e-mail ou téléphone) : il vous suffit de saisir ses coordonnées, sa réponse n’est pas nécessaire.',
  ATTESTATION_REQUIRED: 'Pour candidater, vous devez d’abord attester sur l’honneur exercer pour plus d’un client (attestation absente ou expirée).',
  DEPENDENCE_LIMIT: 'Cet établissement représente déjà plus de la moitié de votre chiffre d’affaires : vous ne pouvez plus candidater chez lui pour le moment.',
  REQUIREMENTS_NOT_MET: 'Votre profil ne remplit pas une exigence obligatoire de cette mission (voir « Profil recherché » ci-dessous).',
  WITHDRAW_PAYMENT_FAILED: 'Le désistement n’a pas pu être enregistré : l’autorisation de paiement n’a pas pu être levée. Réessayez dans un instant.',
  MISSION_NOT_OPEN: 'Cette mission n’est plus ouverte aux candidatures.',
  ALREADY_APPLIED: 'Vous avez déjà candidaté à cette mission.',
};

export function MissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mission, setMission] = useState<Mission | null>(null);
  const [myApplicationStatus, setMyApplicationStatus] = useState<string | null>(null);
  const [mySlot, setMySlot] = useState<{ id: string; status: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    const res = await api.get<{ mission: Mission }>(`/missions/${id}`);
    setMission(res.mission);
    const mine = await api.get<{ missions: Mission[] }>('/missions/mine');
    const match = mine.missions.find((m) => m.id === id);
    setMyApplicationStatus(match?.application_status ?? null);
    if (match?.slot_id) setMySlot({ id: match.slot_id, status: match.slot_status ?? '' });
  }

  useEffect(() => {
    load();
  }, [id]);

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/missions/${id}/applications`);
      navigate(`/extra/missions/${id}/confirmation`);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'REQUEST_FAILED');
    } finally {
      setBusy(false);
    }
  }

  async function withdraw() {
    const hours = mission?.last_minute_cancel_hours ?? 24;
    const startsSoon = mission ? new Date(mission.starts_at).getTime() - Date.now() < hours * 3_600_000 : false;
    const warning = startsSoon
      ? `Cette mission commence dans moins de ${hours} h : un désistement de dernière minute pèse dans votre indice de fiabilité et peut vous faire perdre votre badge. Vous désister quand même ?`
      : 'Vous désister libère la mission pour un autre extra. Confirmer ?';
    if (!window.confirm(warning)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/missions/${id}/withdraw`);
      navigate('/extra');
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'PAYMENT_CANCEL_FAILED' ? 'WITHDRAW_PAYMENT_FAILED' : 'REQUEST_FAILED');
    } finally {
      setBusy(false);
    }
  }

  async function openMessages() {
    setBusy(true);
    setError(null);
    try {
      navigate(`/extra/messages/${await openConversation(id!)}`);
    } catch (err) {
      setError(err instanceof ApiError && MESSAGE_ERRORS[err.code] ? 'MESSAGES_' + err.code : 'REQUEST_FAILED');
    } finally {
      setBusy(false);
    }
  }

  async function toggleCheck(itemId: string, checked: boolean) {
    // Mise à jour immédiate, annulée si le serveur refuse.
    setMission((m) => m && m.briefing ? { ...m, briefing: { ...m.briefing, checklist: m.briefing.checklist.map((c) => (c.id === itemId ? { ...c, checked } : c)) } } : m);
    try {
      await api.post(`/missions/${id}/checklist/${itemId}/check`, { checked });
    } catch {
      await load();
    }
  }

  async function checkIn() {
    if (!mySlot) return;
    setBusy(true);
    try {
      await api.post(`/missions/${id}/slots/${mySlot.id}/check-in`);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function checkOut() {
    if (!mySlot) return;
    setBusy(true);
    try {
      await api.post(`/missions/${id}/slots/${mySlot.id}/check-out`);
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (!mission) {
    return (
      <div className="app-shell">
        <TopBar title="Mission" back />
        <div className="screen-content empty-state">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <TopBar title="Détail de la mission" back />
      <div className="screen-content">
        {error && (
          <div className="error-banner">
            {error.startsWith('MESSAGES_') ? MESSAGE_ERRORS[error.slice(9)] : APPLY_ERRORS[error] ?? error}
            {error === 'REQUIREMENTS_NOT_MET' && (
              <> <Link to="/extra/profil-pro" style={{ fontWeight: 800 }}>Compléter mon profil</Link></>
            )}
            {error === 'EXPERIENCE_REQUIRED' && (
              <> <Link to="/extra/activite/verifications" style={{ fontWeight: 800 }}>Ajouter une expérience</Link></>
            )}
            {(error === 'ATTESTATION_REQUIRED' || error === 'DEPENDENCE_LIMIT') && (
              <> <Link to="/extra/activite" style={{ fontWeight: 800 }}>Voir mon activité</Link></>
            )}
          </div>
        )}

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>{mission.title}</div>
            <span className="badge badge-neutral">{MISSION_STATUS_LABELS[mission.status] ?? mission.status}</span>
          </div>
          {mission.description && <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{mission.description}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
            <MapPin size={14} /> {mission.establishment_name} · {mission.city}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
            <Clock size={14} /> {formatDateRange(mission.starts_at, mission.ends_at)}
          </div>
          <hr className="divider" />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>Rémunération</span>
            <span style={{ fontSize: 15, fontWeight: 800 }}>{formatEuros(mission.hourly_rate_cents)}/h</span>
          </div>
        </div>

        <OrgReputation recommended={mission.organization_recommended} rating={mission.organization_rating} />

        <RequirementsList requirements={mission.requirements} coversCount={mission.covers_count} teamMode={mission.team_mode} match={mission.match} perspective="extra" />
        {mission.match?.blocked && (
          <Link to="/extra/profil-pro" className="btn btn-secondary">Compléter mon profil professionnel</Link>
        )}

        {mission.briefing && (
          <BriefingView
            briefing={mission.briefing}
            startsAt={mission.starts_at}
            audience="extra"
            canCheck={Boolean(mySlot) && ['CONFIRMED', 'IN_PROGRESS', 'COMPLETED'].includes(mission.status)}
            onCheck={toggleCheck}
          />
        )}

        {(mission.status === 'PUBLISHED' || mission.status === 'MATCHING' || myApplicationStatus || mySlot) && (
          <button className="btn btn-secondary" onClick={openMessages} disabled={busy}>
            {myApplicationStatus || mySlot ? 'Écrire à l’établissement' : 'Poser une question à l’établissement'}
          </button>
        )}

        {!myApplicationStatus && (mission.status === 'PUBLISHED' || mission.status === 'MATCHING') && (
          <button className="btn btn-primary" onClick={apply} disabled={busy}>
            {busy ? 'Envoi…' : 'Postuler à cette mission'}
          </button>
        )}

        {myApplicationStatus === 'PENDING' && (
          <div className="badge badge-pending" style={{ justifyContent: 'center' }}>Candidature en attente</div>
        )}

        {mySlot?.status === 'ASSIGNED' && (
          <div className="badge badge-available" style={{ justifyContent: 'center' }}>Mission confirmée — présentez-vous à l'heure</div>
        )}

        {(mySlot?.status === 'ASSIGNED' || mySlot?.status === 'CONFIRMED') && (
          <button className="btn btn-primary" onClick={checkIn} disabled={busy}>
            Pointer mon arrivée
          </button>
        )}
        {mySlot?.status === 'IN_PROGRESS' && (
          <button className="btn btn-primary" onClick={checkOut} disabled={busy}>
            Pointer mon départ
          </button>
        )}
        {mySlot && <HoursPanel missionId={mission.id} side="EXTRA" status={mission.status} onChanged={load} />}

        {mySlot && (mission.status === 'ASSIGNED' || mission.status === 'CONFIRMED') && (
          <button className="btn btn-danger-outline" onClick={withdraw} disabled={busy}>Me désister de cette mission</button>
        )}

        {mySlot && (mission.status === 'VALIDATED' || mission.status === 'CLOSED') && <RatingForm missionId={mission.id} />}

        {mySlot?.status === 'COMPLETED' && (
          <div className="badge badge-available" style={{ justifyContent: 'center' }}>Mission terminée, en attente de validation</div>
        )}
      </div>
    </div>
  );
}
